import { Box, Grid, Typography, Button, LinearProgress, Breadcrumbs, Link } from '@mui/material';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { fundAccess, getPlanList } from '../../redux-store/actions/funds';
import { accessType, FundProps } from '../../redux-store/types/funds';
import { Location } from 'history';
import { YesNoBooleanMaster } from '../../utils/constant';
import { currencyConversion } from '../../utils/utilityFunctions';

function DetailField({
  label,
  value,
  amount,
}: {
  label: string;
  value?: string | number | boolean | null;
  amount?: string | null;
}): JSX.Element {
  const display =
    value === undefined || value === null || value === '' ? '-' : String(value);
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography
        sx={{
          fontSize: 15,
          fontWeight: 500,
          letterSpacing: '0.5px',
          color: 'text.secondary',
          pt: '10px',
          ml: '2px',
        }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 700,
          letterSpacing: '0.5px',
          color: 'text.secondary',
          ml: '2px',
          maxWidth: '100%',
          overflowWrap: 'anywhere',
        }}>
        {display}
      </Typography>
      {amount ? (
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 500,
            letterSpacing: '0.5px',
            color: 'text.secondary',
            pt: 0,
            ml: '2px',
          }}>
          {amount}
        </Typography>
      ) : null}
    </Grid>
  );
}

function SectionHeading({ title }: { title: string }): JSX.Element {
  return (
    <Box sx={{ mb: 3, mt: 1 }}>
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 18,
          fontWeight: 700,
          color: '#2057A6',
          mb: 0.5,
        }}>
        {title}
      </Typography>
    </Box>
  );
}

export default function FundDetails({
  location,
}: {
  location: Location<{ fundId: string }>;
}): JSX.Element {
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(false);
  const [fund, setFund] = useState<FundProps>();
  const [access, setAccess] = useState(false);
  const { fundId } = location.state || {};
  const history = useHistory();

  useEffect(() => {
    let isComponentActive = true;
    (async function () {
      try {
        setLoading(true);
        const { fundSetupAccess } = (await dispatch(fundAccess())) as unknown as accessType;
        if (fundId) {
          const res = (await dispatch(getPlanList(fundId))) as unknown as FundProps;
          setFund(res);
        }
        if (!isComponentActive) {
          return;
        }
        setAccess(fundSetupAccess);
        setLoading(false);
      } catch (e) {
        setLoading(false);
        console.log((e as Error).message);
      }
    })();
    return () => {
      isComponentActive = false;
    };
  }, []);

  return (
    <Box sx={{ bgcolor: 'transparent', px: { xs: 2, sm: 4 }, pt: { xs: 2, sm: 3 }, pb: 3 }}>
      <Breadcrumbs
        separator=">"
        aria-label="breadcrumb"
        sx={{
          mb: 1.5,
          '& .MuiBreadcrumbs-separator': { mx: 0.75, color: '#6C737F' },
          '.MuiTypography-root, .MuiLink-root': {
            fontSize: 14,
            color: '#111927',
            fontFamily: 'Inter, sans-serif',
          },
        }}>
        <Link component={RouterLink} to="/dashboard" underline="hover" color="inherit">
          Home
        </Link>
        <Link
          component={RouterLink}
          to="/funds-setup/fund-list"
          underline="hover"
          color="inherit"
          sx={{ color: '#111927 !important' }}>
          Fund Set-Up
        </Link>
        <Typography sx={{ fontWeight: 500, color: '#6C737F !important' }}>Fund Details</Typography>
      </Breadcrumbs>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          mb: 3,
        }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: { xs: 20, sm: 24 },
            fontWeight: 700,
            color: '#092056',
            lineHeight: 1.2,
          }}>
          Fund Details
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={() => history.push('view-plans', { fundId })}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: 14,
              textTransform: 'none',
              color: '#2057A6',
              borderColor: '#2057A6',
              borderRadius: '8px',
              px: 2,
              py: 1,
              bgcolor: '#FFFFFF',
              '&:hover': { borderColor: '#1A4A8C', bgcolor: '#F8FAFC' },
            }}>
            View Plans
          </Button>
          {access && (
            <Button
              variant="contained"
              onClick={() => history.push('edit-fund', { fundId })}
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: 14,
                textTransform: 'none',
                bgcolor: '#2057A6',
                color: '#FFFFFF',
                borderRadius: '8px',
                px: 2,
                py: 1.25,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#1A4A8C', boxShadow: 'none' },
              }}>
              Edit Details
            </Button>
          )}
        </Box>
      </Box>

      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {fund && (
        <Box
          sx={{
            bgcolor: '#FFFFFF',
            borderRadius: '16px',
            border: '1px solid #F2F4F7',
            boxShadow: '0px 4px 16px rgba(26, 43, 60, 0.04)',
            p: { xs: 2, sm: 3, md: 4 },
          }}>
          <SectionHeading title="Basic Information" />
          <Grid container rowSpacing={3} columnSpacing={4}>
            <DetailField label="Scheme Code" value={fund.schemeCode} />
            <DetailField label="Scheme Name" value={fund.schemeName} />
            <DetailField label="AIF/AMC Code" value={fund.amcCode} />
            <DetailField label="Bank Account Number" value={fund.bankAccountNumber} />
            <DetailField label="Bank Branch" value={fund.bankBranch} />
            <DetailField label="Bank Name" value={fund.bankName} />
            <DetailField label="Bank IFSC Code" value={fund.ifscCode} />
            <DetailField label="CVL Username" value={fund.CVL_USERNAME} />
            <DetailField label="CVL Password" value={fund.CVL_PWD} />
            <DetailField label="CVL Passcode" value={fund.CVL_PASSCODE} />
            <DetailField label="CVL App Pos Code" value={fund.CVL_APP_POS_CODE} />
            <DetailField
              label="CVL Enable New API"
              value={YesNoBooleanMaster[fund?.CVL_ENABLE_NEW_API?.toString() || '']}
            />
            {fund.CVL_ENABLE_NEW_API?.toString() === 'true' && (
              <>
                <DetailField label="CVL JSON AES Key" value={fund?.CVL_JSON_AES_KEY} />
                <DetailField label="CVL JSON API Key" value={fund?.CVL_JSON_API_KEY} />
              </>
            )}
            <DetailField label="Stamp Paper Series" value={fund?.LEEGALITY_STAMP_SERIES} />
            <DetailField label="Stamp Paper Group" value={fund?.LEEGALITY_STAMP_SERIES_GROUP} />
            <DetailField label="Stamp Paper Value" value={fund?.LEEGALITY_STAMP_VALUE} />
            <DetailField
              label="Use Stamp Paper Series"
              value={fund?.LEEGALITY_USE_STAMP_SERIES ? 'Yes' : 'No'}
            />
            <DetailField
              label="Individual Min Networth"
              value={fund?.indMinNetworth}
              amount={currencyConversion(fund?.indMinNetworth, 'inr')}
            />
            <DetailField
              label="Non-Individual Min Networth"
              value={fund?.nonIndMinNetworth}
              amount={currencyConversion(fund?.nonIndMinNetworth, 'inr')}
            />
          </Grid>
        </Box>
      )}
    </Box>
  );
}
